import React, { useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  SelectChangeEvent,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Clear as ClearIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { MaintenanceFilters } from '../../types/maintenance';
import { addDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear } from 'date-fns';

type DateFilterType = 'custom' | 'week' | 'month' | 'year_2025' | 'year_2024' | 'year_2023' | 'year_2022' | 'year_2021' | 'year_2020' | 'year_2019' | 'all';

interface MaintenanceFilterProps {
  filters: MaintenanceFilters;
  onFiltersChange: (filters: MaintenanceFilters) => void;
}

const MaintenanceFilter: React.FC<MaintenanceFilterProps> = ({
  filters,
  onFiltersChange,
}) => {
  const { t } = useTranslation();
  const [dateFilterType, setDateFilterType] = useState<DateFilterType>('custom');

  const handleDateFilterTypeChange = (event: SelectChangeEvent<DateFilterType>) => {
    const value = event.target.value as DateFilterType;
    setDateFilterType(value);

    let startDate: Date | undefined;
    let endDate: Date | undefined;

    const now = new Date();
    now.setHours(0, 0, 0, 0);

    if (value === 'week') {
      startDate = startOfWeek(now, { weekStartsOn: 1 }); // Commence le lundi
      endDate = endOfWeek(now, { weekStartsOn: 1 });
    } else if (value === 'month') {
      startDate = startOfMonth(now);
      endDate = endOfMonth(now);
    } else if (value === 'custom') {
      // Garder les dates personnalisées si elles existent
      startDate = filters.startDate;
      endDate = filters.endDate;
    } else if (value === 'all') {
      // Réinitialiser les dates
      startDate = undefined;
      endDate = undefined;
    } else if (value.startsWith('year_')) {
      // Gérer les années préréglées
      const year = parseInt(value.split('_')[1]);
      startDate = new Date(year, 0, 1); // 1er janvier
      endDate = new Date(year, 11, 31); // 31 décembre
    }

    // Créer un nouvel objet de filtres pour forcer la mise à jour
    const newFilters = {
      ...filters,
      startDate: startDate ? new Date(startDate) : undefined,
      endDate: endDate ? new Date(endDate) : undefined,
    };

    // Supprimer les dates si elles sont undefined pour éviter les problèmes de comparaison
    if (!newFilters.startDate) delete newFilters.startDate;
    if (!newFilters.endDate) delete newFilters.endDate;

    onFiltersChange(newFilters);
  };

  const handleResetDates = () => {
    setDateFilterType('all');
    // Créer un nouvel objet de filtres sans les dates
    const newFilters = { ...filters };
    delete newFilters.startDate;
    delete newFilters.endDate;
    onFiltersChange(newFilters);
  };

  const handleDateChange = (field: 'startDate' | 'endDate') => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    setDateFilterType('custom');

    // Créer un nouvel objet de filtres
    const newFilters = { ...filters };

    if (value) {
      const date = new Date(value);
      if (field === 'startDate') {
        date.setHours(0, 0, 0, 0);
      } else {
        date.setHours(23, 59, 59, 999);
      }
      newFilters[field] = date;
    } else {
      delete newFilters[field];
    }

    onFiltersChange(newFilters);
  };

  const formatDateForInput = (date?: Date) => {
    if (!date) return '';
    return date.toISOString().split('T')[0];
  };

  return (
    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
      <FormControl sx={{ minWidth: 200 }}>
        <InputLabel id="date-filter-type-label">
          {t('maintenance.filters.dateFilterType')}
        </InputLabel>
        <Select
          labelId="date-filter-type-label"
          value={dateFilterType}
          label={t('maintenance.filters.dateFilterType')}
          onChange={handleDateFilterTypeChange}
        >
          <MenuItem value="all">{t('maintenance.filters.dateTypes.all')}</MenuItem>
          <MenuItem value="all">{t('maintenance.filters.dateTypes.all')}</MenuItem>
          <MenuItem value="custom">{t('maintenance.filters.dateTypes.custom')}</MenuItem>
          <MenuItem value="week">{t('maintenance.filters.dateTypes.week')}</MenuItem>
          <MenuItem value="month">{t('maintenance.filters.dateTypes.month')}</MenuItem>
          <MenuItem value="year_2025">{t('maintenance.filters.dateTypes.year_2025')}</MenuItem>
          <MenuItem value="year_2024">{t('maintenance.filters.dateTypes.year_2024')}</MenuItem>
          <MenuItem value="year_2023">{t('maintenance.filters.dateTypes.year_2023')}</MenuItem>
          <MenuItem value="year_2022">{t('maintenance.filters.dateTypes.year_2022')}</MenuItem>
          <MenuItem value="year_2021">{t('maintenance.filters.dateTypes.year_2021')}</MenuItem>
          <MenuItem value="year_2020">{t('maintenance.filters.dateTypes.year_2020')}</MenuItem>
          <MenuItem value="year_2019">{t('maintenance.filters.dateTypes.year_2019')}</MenuItem>
        </Select>
      </FormControl>

      {dateFilterType === 'custom' && (
        <>
          <TextField
            label={t('maintenance.filters.startDate')}
            type="date"
            value={formatDateForInput(filters.startDate)}
            onChange={handleDateChange('startDate')}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 200 }}
          />

          <TextField
            label={t('maintenance.filters.endDate')}
            type="date"
            value={formatDateForInput(filters.endDate)}
            onChange={handleDateChange('endDate')}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 200 }}
          />
        </>
      )}

      {(filters.startDate || filters.endDate) && (
        <Tooltip title={t('maintenance.filters.resetDates')}>
          <IconButton onClick={handleResetDates} size="small">
            <ClearIcon />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export default MaintenanceFilter;